// P1-18 (MR-17). Inline-SVG placeholder image as a data URI. Pure — no network,
// no DOM. The self-hosted replacement for the widget's old runtime third-party
// image placeholder; wired into app.vue empty-playlist (P1-19) and v2
// NowPlayingImage/NowPlayingSection (P1-20). Appearance signed off 2026-07-16.

// XML-escape so the label can't break the SVG markup (the whole SVG is then
// encodeURIComponent'd for the data URI, but the browser decodes it back, so
// the raw markup must already be valid XML).
const xmlEscape = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Strip C0 control chars that are illegal in XML 1.0 (tab 0x09 / LF 0x0a /
// CR 0x0d are kept). Code-point comparison rather than a control-char regex
// literal, for clarity; Array.from iterates by code point so valid surrogate
// pairs (emoji) stay intact.
const stripControls = (s) => Array.from(String(s))
  .filter((ch) => {
    const c = ch.charCodeAt(0);
    return c > 0x1f || c === 0x09 || c === 0x0a || c === 0x0d;
  })
  .join('');

/**
 * Build a neutral placeholder image as an inline-SVG data URI.
 *
 * @param {number} width  image width in px
 * @param {number} height image height in px
 * @param {string} text   centered label
 * @return {string} `data:image/svg+xml,<encoded svg>`
 */
// eslint-disable-next-line import/prefer-default-export
export const placeholderImage = (width, height, text = '') => {
  const w = Number.isFinite(Number(width)) ? Number(width) : 0;
  const h = Number.isFinite(Number(height)) ? Number(height) : 0;
  // Legible label sized to the box; clamped so tiny/huge boxes stay sane.
  const fontSize = Math.max(10, Math.min(48, Math.round(Math.min(w, h) / 8)));
  const svgFor = (label) => `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`
    + '<rect width="100%" height="100%" fill="#dadae0"/>'
    + '<text x="50%" y="50%" fill="#6e6e7a" font-family="Helvetica,Arial,sans-serif" '
    + `font-size="${fontSize}" text-anchor="middle" dominant-baseline="middle">${xmlEscape(label)}</text>`
    + '</svg>';
  const encode = (label) => `data:image/svg+xml,${encodeURIComponent(svgFor(label))}`;
  try {
    return encode(stripControls(text));
  } catch (e) {
    // Never throw from a fallback image util: pathological text (e.g. an unpaired
    // UTF-16 surrogate, which makes encodeURIComponent throw) degrades to a
    // blank-label placeholder — still a valid data URI.
    return encode('');
  }
};
